import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FormInput from '../../../shared/components/FormInput';
import TicketingSettingsFields from './TicketingSettingsFields.jsx';

function buildInitialFields(detail) {
  const values = {};
  (detail?.setup_fields || []).forEach((field) => {
    values[field.key] = detail?.[field.key] ?? field.default ?? '';
  });
  return values;
}

// Seeded from what the server already stores, falling back to the schema default, so saving
// without touching a field cannot silently change it.
function buildInitialSettings(detail) {
  const values = {};
  (detail?.settings_schema || []).forEach((field) => {
    const stored = detail?.settings?.[field.key];
    values[field.key] = stored === undefined ? field.default : stored;
  });
  return values;
}

function buildInitialCredentials(detail) {
  const values = {};
  (detail?.setup_schema || []).forEach((field) => {
    values[field.key] = '';
  });
  return values;
}

export default function IntegrationSettingsForm({ detail, saving, onSave }) {
  const [fields, setFields] = useState(() => buildInitialFields(detail));
  const [credentials, setCredentials] = useState(() => buildInitialCredentials(detail));
  const [syncEnabled, setSyncEnabled] = useState(Boolean(detail?.sync_enabled));
  const [settings, setSettings] = useState(() => buildInitialSettings(detail));
  const [formError, setFormError] = useState('');

  useEffect(() => {
    setFields(buildInitialFields(detail));
    setCredentials(buildInitialCredentials(detail));
    setSyncEnabled(Boolean(detail?.sync_enabled));
    setSettings(buildInitialSettings(detail));
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset form on integration id change only
  }, [detail?.id]);

  const credentialFields = detail?.setup_schema || [];
  const instanceFields = detail?.setup_fields || [];

  const configuredKeys = useMemo(
    () => new Set(detail?.credential_keys || []),
    [detail?.credential_keys]
  );

  const handleFieldChange = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleCredentialChange = (key, value) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      const patch = {};
      instanceFields.forEach((field) => {
        patch[field.key] = fields[field.key];
      });

      const credPatch = {};
      credentialFields.forEach((field) => {
        const value = credentials[field.key];
        if (value) credPatch[field.key] = value;
      });

      const payload = { ...patch, sync_enabled: syncEnabled };
      if ((detail?.settings_schema || []).length > 0) payload.settings = settings;

      await onSave(payload, credPatch);
    } catch (err) {
      setFormError(err.message || 'Save failed');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
      {formError && (
        <div className="alert alert-danger py-2 mb-0" role="alert">
          {formError}
        </div>
      )}

      {instanceFields.map((field) => (
        <FormInput
          key={field.key}
          id={`integration-field-${field.key}`}
          label={field.label}
          type={field.type === 'url' ? 'url' : 'text'}
          value={fields[field.key] ?? ''}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          required={field.required}
        />
      ))}

      {credentialFields.length > 0 && (
        <div>
          <h6 className="text-muted fw-normal mb-2">Credentials</h6>
          {credentialFields.map((field) => (
            <FormInput
              key={field.key}
              id={`integration-credential-${field.key}`}
              label={field.label}
              type="password"
              value={credentials[field.key] ?? ''}
              onChange={(e) => handleCredentialChange(field.key, e.target.value)}
              placeholder={
                configuredKeys.has(field.key)
                  ? 'Leave blank to keep existing value'
                  : field.required
                    ? 'Required'
                    : 'Optional'
              }
              helperText={
                configuredKeys.has(field.key)
                  ? 'A value is already stored for this integration.'
                  : undefined
              }
              className="mb-3"
            />
          ))}
        </div>
      )}

      <TicketingSettingsFields detail={detail} values={settings} onChange={handleSettingChange} />

      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          id="integration-sync-enabled"
          checked={syncEnabled}
          onChange={(e) => setSyncEnabled(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="integration-sync-enabled">
          Enable daily background sync
        </label>
      </div>

      <div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </form>
  );
}

IntegrationSettingsForm.propTypes = {
  detail: PropTypes.object,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};
